import type { List, Map } from 'immutable';

import {
  Entity,
  type EntityFields,
  type EntityRecord,
  Fields,
} from '@burnsred/entity';
import { default as DjangoRestFramework } from '@burnsred/entity-duck-namespace-drf';

import { duckAppName } from '@/constants';
import UserEntity, { type UserEntityRecord } from '@/entities/User';

import ApplicationBuildEntity, {
  type ApplicationBuildData,
  type ApplicationBuildEntityRecord,
} from './ApplicationBuild';
import ApplicationImageEntity, {
  type ApplicationImageData,
  type ApplicationImageEntityRecord,
} from './ApplicationImage';
import BusinessAreaEntity, {
  type BusinessAreaEntityRecord,
} from './BusinessArea';
import CategoryEntity, { type CategoryEntityRecord } from './Category';
import type { DateTimeValue } from './dates';
import type {
  ApplicationLlmData,
  MessageSenderData,
  MessagingPartnerData,
} from './features';
import { FilerImageEntity, type FilerImageEntityRecord } from './Filer';

class ApplicationEntity extends Entity {
  static paginated = true;

  static paths = {
    apiBase: '/applications/v1/application/',
  };

  static fields: EntityFields<ApplicationEntityFields> = {
    uuid: new Fields.IdField(),
    name: new Fields.CharField(),
    slug: new Fields.CharField({ blank: true }),
    preview_title: new Fields.CharField(),
    description: new Fields.TextField({ blank: true }),
    owner: new Fields.EntityField({ entity: UserEntity, blank: true }),
    technical_owner: new Fields.EntityField({
      entity: UserEntity,
      blank: true,
    }),
    business_area: new Fields.EntityField({ entity: BusinessAreaEntity }),
    department: new Fields.CharField({ blank: true }),
    category: new Fields.EntityField({ entity: CategoryEntity, blank: true }),
    maturity: new Fields.CharField({ default: 'prototype' }),
    deployment_environment: new Fields.CharField({ default: 'development' }),
    access_type: new Fields.CharField(),
    deployment_method: new Fields.CharField({ blank: true }),
    external_url: new Fields.CharField({ blank: true }),
    visibility: new Fields.CharField({ default: 'private' }),
    is_archived: new Fields.BooleanField({ default: false }),
    messaging_enabled: new Fields.BooleanField({ default: false }),
    llm_enabled: new Fields.BooleanField({ default: false }),
    message_senders: new Fields.AnyField({ blank: true }),
    message_recipients: new Fields.AnyField({ blank: true, local: true }),
    llm: new Fields.AnyField({ blank: true, local: true }),
    logo: new Fields.EntityField({ entity: FilerImageEntity, blank: true }),
    view_count: new Fields.IntegerField({ blank: true, local: true }),
    status: new Fields.CharField({ blank: true, local: true }),
    url: new Fields.CharField({ blank: true, local: true }),
    hosted_url_preview: new Fields.CharField({ blank: true, local: true }),
    current_build: new Fields.EntityField({
      entity: ApplicationBuildEntity,
      blank: true,
      local: true,
    }),
    images: new Fields.EntityField({
      entity: ApplicationImageEntity,
      many: true,
      blank: true,
      local: true,
    }),
    can_edit: new Fields.BooleanField({ blank: true, local: true }),
    can_delete: new Fields.BooleanField({ blank: true, local: true }),
    created: new Fields.DateTimeField({ blank: true, local: true }),
    modified: new Fields.DateTimeField({ blank: true, local: true }),
  };

  static dataToRecord(data: Partial<ApplicationData>) {
    return super.dataToRecord(data) as ApplicationEntityRecord;
  }

  static toString(record: ApplicationEntityRecord | null | undefined) {
    return record?.get('name') ?? '';
  }
}

export type ApplicationEntityFields = {
  uuid: string;
  name: string;
  slug: string;
  preview_title: string;
  description: string;
  owner: UserEntityRecord | null;
  technical_owner: UserEntityRecord | null;
  business_area: BusinessAreaEntityRecord | null;
  department: string;
  category: CategoryEntityRecord | null;
  maturity: string;
  deployment_environment: string;
  access_type: string;
  deployment_method: string;
  external_url: string;
  visibility: string;
  is_archived: boolean;
  messaging_enabled: boolean;
  llm_enabled: boolean;
  message_senders: List<Map<string, unknown>> | null;
  message_recipients: List<Map<string, unknown>> | null;
  llm: Map<string, unknown> | null;
  logo: FilerImageEntityRecord | null;
  view_count: number;
  status: string;
  url: string | null;
  hosted_url_preview: string;
  current_build: ApplicationBuildEntityRecord | null;
  images: List<ApplicationImageEntityRecord> | null;
  can_edit: boolean;
  can_delete: boolean;
  created: DateTimeValue;
  modified: DateTimeValue;
};

export type ApplicationEntityRecord = EntityRecord<ApplicationEntityFields>;

export type UserReferenceData = {
  id: number | string;
  uuid?: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
};

export type LookupData = { uuid: string; name: string };

export type FilerImageData = {
  id: number | string;
  name: string;
  url: string;
  thumbnails?: Record<string, string>;
};

export type ApplicationData = {
  uuid: string;
  name: string;
  slug: string;
  preview_title: string;
  description: string;
  owner: UserReferenceData;
  technical_owner: UserReferenceData | null;
  business_area: LookupData;
  department: string;
  category: LookupData | null;
  maturity: string;
  deployment_environment: string;
  access_type: string;
  deployment_method: string;
  external_url: string;
  visibility: string;
  is_archived: boolean;
  messaging_enabled: boolean;
  llm_enabled: boolean;
  message_senders: MessageSenderData[];
  message_recipients: MessagingPartnerData[];
  llm: ApplicationLlmData | null;
  logo: FilerImageData | null;
  view_count: number;
  status: string;
  url: string | null;
  hosted_url_preview: string;
  current_build: Omit<ApplicationBuildData, 'application'> | null;
  images: Omit<ApplicationImageData, 'application'>[];
  can_edit: boolean;
  can_delete: boolean;
  created: string;
  modified: string;
};

ApplicationEntity.duck = new DjangoRestFramework({
  app: duckAppName,
  entity: ApplicationEntity,
  name: 'ApplicationEntity',
});

export default ApplicationEntity;
